/**
 * Tasks: things the business has to get done. Unlike an appointment, a task doesn't take
 * calendar time (unless it reserves some) and stays open until it's marked done.
 */
export enum TaskStatusEnum {
  Open = 'open',
  Done = 'done',
}

/** Where a task came from. */
export enum TaskSourceEnum {
  /** Created by a person. */
  Manual = 'manual',
  /** Opened by the system (see TaskAutoTypeEnum). */
  Auto = 'auto',
  /** Generated from a service's checklist template for an appointment. */
  Checklist = 'checklist',
}

/** What made the system open an automatic task. */
export enum TaskAutoTypeEnum {
  /** The WhatsApp bot handed a conversation to a person. Closes when the owner replies. */
  BotHandoff = 'bot-handoff',
  /** A customer with an open debt has an appointment today. Closes when the balance is settled. */
  CustomerDebt = 'customer-debt',
  /** A consent form the service requires isn't signed for tomorrow's appointment. Closes when it's signed. */
  MissingConsentForm = 'missing-consent-form',
  /** A customer left a negative review. */
  NegativeReview = 'negative-review',
  /** A WhatsApp message to a customer was not delivered and no SMS went out instead. */
  FailedMessage = 'failed-message',
}

export enum TaskRecurrenceEnum {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

/** A checklist task's place on its appointment. */
export enum TaskChecklistPhaseEnum {
  Before = 'before',
  After = 'after',
}

/** Calendar time a task holds, blocking it for booking. Hours are decimal (13.5 = 13:30). */
export interface ITaskReservation {
  from: number;
  to: number;
  /** The blocking calendar event (a manual appointment carrying `taskId`). */
  appointmentId?: string;
}

export interface ITask {
  id?: string;
  bid?: string;
  title: string;
  notes?: string;
  status: TaskStatusEnum;
  /** Due day, yyyy-MM-dd in Israel time. Absent = no date. */
  dueDate?: string | null;
  /** Due time as a decimal hour, only with a dueDate. */
  dueTime?: number | null;
  /** When to push a reminder to the assignee. */
  reminderAt?: Date | string | null;
  reminderSentAt?: Date | string | null;
  /** userId of the employee responsible. Absent = the business owner. */
  assigneeId?: string | null;
  assigneeName?: string;
  createdBy?: string;
  customerId?: string | null;
  customerName?: string;
  appointmentId?: string | null;
  checklistPhase?: TaskChecklistPhaseEnum;
  source: TaskSourceEnum;
  autoType?: TaskAutoTypeEnum;
  /** Link the task's action opens (WhatsApp chat, review, customer page). */
  actionUrl?: string;
  actionLabel?: string;
  recurrence?: TaskRecurrenceEnum | null;
  reservation?: ITaskReservation | null;
  completedAt?: Date | string | null;
  completedBy?: string | null;
  completedByName?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/** What the dashboard sends to create / edit a task. */
export type ITaskInput = Pick<ITask,
  'title' | 'notes' | 'dueDate' | 'dueTime' | 'reminderAt' | 'assigneeId' | 'customerId' | 'appointmentId' | 'recurrence'
> & {
  /** Reserve calendar time: minutes from dueTime. 0 / absent = no reservation. */
  reserveMinutes?: number;
};

export type TTasksScope = 'mine' | 'team' | 'done';

export interface ITasksCounts {
  /** Open tasks in "mine" due today or earlier. */
  dueNow: number;
  overdue: number;
}

/** A service's task template, copied onto each of its appointments. */
export interface IServiceTaskChecklist {
  before: string[];
  /** `daysAfter` > 0 makes it a follow-up task for the customer, due that many days after the visit. */
  after: Array<{ title: string; daysAfter?: number }>;
}

export const TASK_AUTO_TYPE_LABELS: Record<TaskAutoTypeEnum, string> = {
  [TaskAutoTypeEnum.BotHandoff]: 'בוט',
  [TaskAutoTypeEnum.CustomerDebt]: 'חוב פתוח',
  [TaskAutoTypeEnum.MissingConsentForm]: 'טופס חסר',
  [TaskAutoTypeEnum.NegativeReview]: 'משוב שלילי',
  [TaskAutoTypeEnum.FailedMessage]: 'הודעה שלא נמסרה',
};

export const TASK_RECURRENCE_LABELS: Record<TaskRecurrenceEnum, string> = {
  [TaskRecurrenceEnum.Daily]: 'כל יום',
  [TaskRecurrenceEnum.Weekly]: 'כל שבוע',
  [TaskRecurrenceEnum.Monthly]: 'כל חודש',
};
